import { Component, createRef } from "react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn } from "namirasoft-site-react";
import { CloudRow, CloudInputRow, CloudType, CloudConnectType, ServerRow, ProjectRow, EnvironmentRow } from "namirasoft-infra";
import { AWSRow, AzureRow, GCPRow } from "namirasoft-credential";
import { InfraLayout } from '../InfraLayout';
import { InfraEntities } from "../InfraEntities";
import { InfraRouterProps } from "../InfraRouterProps";
import { NamirasoftMap } from "namirasoft-site-map";
import { LogGroupRow } from "namirasoft-log";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";

interface CloudEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface CloudEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    type?: CloudType;
    connect_type?: CloudConnectType;
}

export default class CloudEditPage extends Component<CloudEditPageProps, CloudEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_CloudType = createRef<NSBoxEnum<CloudType>>();
    NSBoxEnum_ConnectType = createRef<NSBoxEnum<CloudConnectType>>();
    NSBoxEntity_ConnectDirectCredentialID = createRef<NSBoxEntity<AWSRow | AzureRow | GCPRow>>();
    NSBoxEntity_ConnectServerCliOnServerID = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: CloudEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<CloudRow, CloudInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.cloud(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): CloudInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_CloudType.current?.getValueEnumOne(CloudType, CloudType.AWS) ?? CloudType.AWS;
                                        let connect_type = this.NSBoxEnum_ConnectType.current?.getValueEnumOne(CloudConnectType, CloudConnectType.Direct) ?? CloudConnectType.Direct;
                                        let connect_direct_credential_id = this.NSBoxEntity_ConnectDirectCredentialID.current?.getValueOne(this.state.connect_type === CloudConnectType.Direct) ?? null;
                                        let connect_server_cli_on_server_id = this.NSBoxEntity_ConnectServerCliOnServerID.current?.getValueOne(this.state.connect_type === CloudConnectType.ByServer) ?? null;
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            type,
                                            connect_type,
                                            connect_direct_credential_id,
                                            connect_server_cli_on_server_id,
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            description,
                                            cloud_category: [],
                                            cloud_field: [],
                                            cloud_tag: [],
                                        };
                                    },
                                    setRow: (row: CloudRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_CloudType.current?.setValueOne(row.type);
                                        this.NSBoxEnum_ConnectType.current?.setValueOne(row.connect_type);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                type: row.type,
                                                connect_type: row.connect_type,
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_ConnectDirectCredentialID.current?.setValueOne(row.connect_direct_credential_id);
                                                this.NSBoxEntity_ConnectServerCliOnServerID.current?.setValueOne(row.connect_server_cli_on_server_id);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Connect",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<CloudConnectType>
                                                        ref={this.NSBoxEnum_ConnectType}
                                                        title="Connect Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => CloudConnectType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ connect_type: box.getValueOne() as CloudConnectType }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.connect_type === CloudConnectType.Direct &&
                                                        <>
                                                            <NSBoxEntity<AWSRow | AzureRow | GCPRow>
                                                                ref={this.NSBoxEntity_ConnectDirectCredentialID}
                                                                title={`${this.state.type ? NamingConvention.auto.convert(this.state.type, NamingConvention.Pascal_Case_Space) + " " : ""}Credential`}
                                                                required
                                                                multiple={false}
                                                                table_name={this.state.type?.toLowerCase() ?? "aws"}
                                                                list={async (filters, page, size, sorts) =>
                                                                {
                                                                    let handlers: Record<string, any> = {
                                                                        [CloudType.AWS]: this.props.servers.credential.aws.List,
                                                                        [CloudType.Azure]: this.props.servers.credential.azure.List,
                                                                        [CloudType.GCP]: this.props.servers.credential.gcp.List,
                                                                    };

                                                                    let list_handler = this.state.type ? handlers[this.state.type] : null;

                                                                    if (!list_handler)
                                                                        throw new Error(`Invalid Cloud type: ${this.state.type}`);

                                                                    return await list_handler(filters, page, size, sorts);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            {/* TODO: Replace with correct cloud connect-type images */}
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/direct.png"
                                                                alt="Direct"
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.connect_type === CloudConnectType.ByServer &&
                                                        <>
                                                            <NSBoxEntity<ServerRow>
                                                                ref={this.NSBoxEntity_ConnectServerCliOnServerID}
                                                                title="CLI On Server"
                                                                required
                                                                multiple={false}
                                                                table_name="server"
                                                                list={async (filters, page, size, sorts) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.server,
                                                                            namirasoft.infra.tables.server.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            {/* TODO: Replace with correct cloud connect-type images */}
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/by-server.png"
                                                                alt="By Server"
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        title="Name"
                        required
                        // info={{ link: "https://namirasoft.com/infra/console-guide/cloud/#field-name" }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_ConnectServerCliOnServerID.current?.setValue(null);
                                    this.NSBoxEntity_ConnectServerCliOnServerID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEnum<CloudType>
                        ref={this.NSBoxEnum_CloudType}
                        title="Cloud Type"
                        required
                        multiple={false}
                        getEnumObject={() => CloudType}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ type: box.getValueOne() as CloudType }),
                                () => this.NSBoxEntity_ConnectDirectCredentialID.current?.reload(),
                            );
                        }}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/cloud/#field-cloud-type" }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required
                        title="Description"
                        // info={{ link: "https://namirasoft.com/infra/console-guide/cloud/#field-description" }}
                        style={{ width: NSBox.width.double }}
                        min_length={50}
                    />
                </NSColumn>
            </InfraLayout>
        );
    }
}